import { useId } from 'react';

/**
 * Lift2Fit logo system — inline SVG so colour follows the theme.
 *
 *   Logo                        stacked LIFT / 2FIT mark      (brand, footer, splash)
 *   Logo variant="horizontal"   one-line LIFT2FIT            (site header — regular T)
 *   Logo variant="horizontal" tStyle="plated"  the plate-stacked T (kept for later)
 *   LogoMark                    app-icon tile: the barbell symbol on charcoal
 *   BarbellGlyph                the symbol alone (section markers, loaders)
 *
 * Geometry is generated by brand/logo/generate-logos.mjs — keep them in sync.
 *
 * THE MARK
 *   • Letters weight 34 on a 100-unit cap height. The numeral 2 keeps its own
 *     26 stroke and the F its own 28, so both stay clean at that weight.
 *   • The T is a PLATE STACK INSIDE the cap height — outer plate, gap, crossbar,
 *     stem to the baseline — so no glyph is taller than any other.
 *   • The crossbar is exactly as wide as the F's top bar; only the outer plate is
 *     narrower (78 %), the way a 10 kg plate sits inside a 5 kg.
 *   • The T at the foot of 2FIT is an exact 180° mirror of the one in LIFT.
 *   • Every I is a dumbbell.
 *   • COLOUR: LIFT and FIT take the theme's ink (black on light, white on dark);
 *     only the 2 carries the orange.
 */

const ORANGE = '#FF5722';
const INK = '#111111';

const LETTER_STROKE = 34;
const TWO_STROKE = 26;
const F_STROKE = 28;
const CAP = 100;
const GAP_X = 10;
const STEM_GAP = 18;
const BOX = 92;
const IBOX = 60;

const PLATE_H = 9;
const PLATE_GAP = 7;
const PLATE_W = Math.round(BOX * 0.78);
const CROSSBAR_TOP = PLATE_H + PLATE_GAP;
const TWO_R = (BOX - TWO_STROKE) / 2;
const stemX = (b: number, s: number) => (b - s) / 2;

type Rect = { x: number; y: number; w: number; h: number };
const r = (x: number, y: number, w: number, h: number): Rect => ({ x, y, w, h });
/** mirror vertically inside the cap height — x MUST be preserved */
const mirror = (box: Rect): Rect => r(box.x, CAP - box.y - box.h, box.w, box.h);

const PLAIN_T = [r(0, 0, BOX, LETTER_STROKE), r(stemX(BOX, LETTER_STROKE), 0, LETTER_STROKE, CAP)];
const PLAIN_T_INV = [mirror(PLAIN_T[1]), mirror(PLAIN_T[0])];

const STACK_TOP = r((BOX - PLATE_W) / 2, 0, PLATE_W, PLATE_H);
const STACK_BAR = r(0, CROSSBAR_TOP, BOX, LETTER_STROKE);
const STACK_STEM = r(stemX(BOX, LETTER_STROKE), CROSSBAR_TOP, LETTER_STROKE, CAP - CROSSBAR_TOP);
const STACK_INV = [mirror(STACK_STEM), mirror(STACK_BAR), mirror(STACK_TOP)];

const GLYPH: Record<string, { w: number; rects?: Rect[]; path?: string; stroked?: boolean }> = {
  L: { w: BOX, rects: [r(0, 0, LETTER_STROKE, CAP), r(0, CAP - LETTER_STROKE, BOX, LETTER_STROKE)] },
  F: {
    w: BOX,
    rects: [
      r(0, 0, F_STROKE, CAP),
      r(0, 0, BOX, F_STROKE),
      r(0, Math.round(CAP * 0.36), Math.round(BOX * 0.72), F_STROKE),
    ],
  },
  T: { w: BOX, rects: [STACK_TOP, STACK_BAR, STACK_STEM] },
  T_INV: { w: BOX, rects: STACK_INV },
  T_PLAIN: { w: BOX, rects: PLAIN_T },
  T_PLAIN_INV: { w: BOX, rects: PLAIN_T_INV },
  TWO: {
    w: BOX,
    stroked: true,
    path: `M${TWO_STROKE / 2} ${TWO_R + TWO_STROKE / 2} A${TWO_R} ${TWO_R} 0 0 1 ${
      BOX - TWO_STROKE / 2
    } ${TWO_R + TWO_STROKE / 2} L${TWO_STROKE / 2} ${CAP - TWO_STROKE / 2} H${BOX - TWO_STROKE / 2}`,
  },
};

/** the dumbbell I: shaft + one plate cap per end, inside the box */
const DUMBBELL: Rect[] = [
  r(1, 0, IBOX - 2, LETTER_STROKE),
  r(stemX(IBOX, LETTER_STROKE), LETTER_STROKE, LETTER_STROKE, CAP - LETTER_STROKE * 2),
  r(1, CAP - LETTER_STROKE, IBOX - 2, LETTER_STROKE),
];

const glyphWidth = (key: string) => (key === 'I' ? IBOX : GLYPH[key]?.w ?? BOX);

function layout(keys: string[]) {
  let x = 0;
  const parts = keys.map((key) => {
    const part = { key, x, w: glyphWidth(key) };
    x += part.w + GAP_X;
    return part;
  });
  return { parts, width: x - GAP_X };
}

const L1 = layout(['L', 'I', 'F', 'T']);
const L2 = layout(['TWO', 'F', 'I', 'T_INV']);
const LINE = layout(['L', 'I', 'F', 'T', 'TWO', 'F', 'I', 'T']);
const LINE2_Y = CAP + STEM_GAP;

function Word({
  parts,
  y = 0,
  fill,
  twoFill,
  tStyle = 'plated',
}: {
  parts: ReturnType<typeof layout>['parts'];
  y?: number;
  fill: string;
  /** the numeral 2 is the only element that carries the brand orange */
  twoFill?: string;
  /** `plain` draws a regular T — the site header; `plated` is the brand mark */
  tStyle?: 'plain' | 'plated';
}) {
  return (
    <>
      {parts.map((part, index) => {
        const glyphFill = part.key === 'TWO' && twoFill ? twoFill : fill;
        if (part.key === 'I') {
          return (
            <g key={`${part.key}-${index}`} transform={`translate(${part.x},${y})`} fill={glyphFill}>
              {DUMBBELL.map((b, i) => (
                <rect key={i} x={b.x} y={b.y} width={b.w} height={b.h} />
              ))}
            </g>
          );
        }
        const key = tStyle === 'plain' && (part.key === 'T' || part.key === 'T_INV')
          ? part.key === 'T'
            ? 'T_PLAIN'
            : 'T_PLAIN_INV'
          : part.key;
        const glyph = GLYPH[key];
        if (!glyph) return null;
        if (glyph.stroked) {
          return (
            <g
              key={`${part.key}-${index}`}
              transform={`translate(${part.x},${y})`}
              fill="none"
              stroke={glyphFill}
              strokeWidth={TWO_STROKE}
              strokeLinecap="butt"
              strokeLinejoin="miter"
            >
              <path d={glyph.path} />
            </g>
          );
        }
        return (
          <g key={`${part.key}-${index}`} transform={`translate(${part.x},${y})`} fill={glyphFill}>
            {(glyph.rects ?? []).map((b, i) => (
              <rect key={i} x={b.x} y={b.y} width={b.w} height={b.h} />
            ))}
          </g>
        );
      })}
    </>
  );
}

export interface LogoProps {
  className?: string;
  /** `stacked` is the brand mark; `horizontal` is the one-line site header lockup. */
  variant?: 'stacked' | 'horizontal';
  /**
   * Only affects the one-line lockup. `plain` (default) draws the regular T the
   * header ships; `plated` draws the plate-stacked T of the brand mark.
   */
  tStyle?: 'plain' | 'plated';
  title?: string;
}

export function Logo({
  className,
  variant = 'stacked',
  tStyle = 'plain',
  title = 'Lift2Fit',
}: LogoProps) {
  if (variant === 'horizontal') {
    const b = { x: -12, y: -12, w: LINE.width + 24, h: CAP + 24 };
    return (
      <svg
        viewBox={`${b.x} ${b.y} ${b.w} ${b.h}`}
        role="img"
        aria-label={title}
        className={className}
        preserveAspectRatio="xMinYMid meet"
      >
        <Word
          parts={LINE.parts.filter((p) => p.x < (LINE.parts[4]?.x ?? 0))}
          fill="currentColor"
          tStyle={tStyle}
        />
        <Word parts={LINE.parts.slice(4)} fill="currentColor" twoFill={ORANGE} tStyle={tStyle} />
      </svg>
    );
  }

  const b = { x: -12, y: -12, w: L1.width + 24, h: LINE2_Y + CAP + 24 };
  return (
    <svg
      viewBox={`${b.x} ${b.y} ${b.w} ${b.h}`}
      role="img"
      aria-label={title}
      className={className}
      preserveAspectRatio="xMinYMid meet"
    >
      <Word parts={L1.parts} fill="currentColor" />
      <Word parts={L2.parts} y={LINE2_Y} fill="currentColor" twoFill={ORANGE} />
    </svg>
  );
}

function symbolShapes(topFill: string, bottomFill: string) {
  return (
    <>
      <g fill={topFill}>
        {[STACK_TOP, STACK_BAR, STACK_STEM].map((s, i) => (
          <rect key={`t-${i}`} x={s.x} y={s.y} width={s.w} height={s.h} />
        ))}
      </g>
      <g fill={bottomFill}>
        {STACK_INV.map((s, i) => (
          <rect key={`ti-${i}`} x={s.x} y={s.y + LINE2_Y} width={s.w} height={s.h} />
        ))}
      </g>
    </>
  );
}

const SYM_BOX = { x: -6, y: -6, w: BOX + 12, h: LINE2_Y + CAP + 12 };

/** The barbell symbol on its own — section markers, empty states, loaders. */
export function BarbellGlyph({ className, title }: { className?: string; title?: string }) {
  return (
    <svg
      viewBox={`${SYM_BOX.x} ${SYM_BOX.y} ${SYM_BOX.w} ${SYM_BOX.h}`}
      role={title ? 'img' : 'presentation'}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      className={className}
    >
      {symbolShapes('currentColor', 'currentColor')}
    </svg>
  );
}

/** App icon / favicon: the symbol on a charcoal tile. */
export function LogoMark({ className, title = 'Lift2Fit' }: { className?: string; title?: string }) {
  const tileId = useId().replace(/:/g, '');
  const scale = Math.min(320 / SYM_BOX.w, 320 / SYM_BOX.h);
  return (
    <svg viewBox="0 0 512 512" role="img" aria-label={title} className={className}>
      <rect id={`${tileId}-tile`} width="512" height="512" rx="112" fill="var(--l2f-mark-tile, #111111)" />
      <g
        transform={`translate(${(512 - SYM_BOX.w * scale) / 2 - SYM_BOX.x * scale},${
          (512 - SYM_BOX.h * scale) / 2 - SYM_BOX.y * scale
        }) scale(${scale})`}
      >
        {symbolShapes('var(--l2f-mark-glyph, #ff5722)', 'var(--l2f-mark-glyph, #ff5722)')}
      </g>
    </svg>
  );
}

export { INK as LOGO_INK, ORANGE as LOGO_ORANGE };
